import {
  ActivityFeedEntry,
  BotMessageSubtype,
  ChatSummary,
  InProgressMessage,
  Intent,
  MessageMarker,
} from "@/types/chat"
import { CollectionSummary } from "@/types/collection"
import { Airline } from "@/types/flights/airline"
import { InspirationSummary } from "@/types/inspiration"
import { Itinerary } from "@/types/itinerary"
import { LocationSearchHit, PlaceSummary } from "@/types/place"
import {
  AgentStatusEntityPreviewItem,
  AgentStatusEntityPreviewVisualization,
  AgentStatusFlightNotePreviewItem,
  AgentStatusFlightNotePreviewVisualization,
  AgentStatusFlightSearchPreviewItem,
  AgentStatusFlightSearchPreviewVisualization,
  AgentStatusLoyaltyProgressVisualization,
  AgentStatusQuotePreviewItem,
  AgentStatusQuotePreviewVisualization,
  AgentStatusUpdateEventData,
  AgentStatusVisualization,
} from "@/types/socket"
import {
  DelayChunk,
  MessageChunk,
  TextChunk,
  UiSignalChunk,
} from "@/types/streamed-messages"
import { Trip, TripChange } from "@/types/trip"

import { warnUnreachable } from "@/lib/utils/assertions"
import { now, nowMillis } from "./date-utils"

function shouldSuppressActivityFeed(chunk: DelayChunk): boolean {
  return chunk.delay_reason === "update_itinerary" && Boolean(chunk.delay_place)
}

export class StreamedMessageBuilder {
  lastUserVisibleChunk: TextChunk | DelayChunk | undefined

  places: PlaceSummary[] = []
  inspirations: InspirationSummary[] = []
  airlines: Airline[] = []
  collections: CollectionSummary[] = []
  body: string = ""
  markers: MessageMarker[] = []
  itinerary: Itinerary | undefined
  chat: ChatSummary | undefined
  trip: Trip | undefined
  isGeneratingItinerary = false
  tripChange: TripChange | undefined
  searchGeo: LocationSearchHit | undefined
  intent: Intent | undefined
  activityFeed: ActivityFeedEntry[] = []
  thinkingSkipped = false
  structuredResults: InProgressMessage["structured_results"] | undefined

  private lastFeedDelayText: string | undefined
  private lastEntityPreviewCount = 0
  private lastQuotePreviewCount = 0
  private lastAgentSessionId: number | undefined
  private firstDelayAtMs: number | undefined
  private lastDelayAtMs: number | undefined
  private currentMessageFlightPreviewSeen = false

  // Track UI signal state during streaming (skeleton → complete phases)
  private uiSignals: Array<{
    signalId: string
    signalType: string
    start: number
    end: number
    payload: Record<string, unknown>
    isComplete: boolean
  }> = []

  constructor(private sub_type?: BotMessageSubtype) {}

  skipThinking() {
    this.thinkingSkipped = true
    this.activityFeed = []
    this.firstDelayAtMs = undefined
    this.lastDelayAtMs = undefined
    this.currentMessageFlightPreviewSeen = false
  }

  // Throw chunks into this and at any point it can build an in-progress
  // BotMessage for displaying in the single-message.tsx component.
  addChunk(chunk: MessageChunk) {
    if (!chunk) {
      throw new Error("Bad chunk")
    }
    const type = chunk.type
    switch (type) {
      case "text":
        this.body = this.body.concat(chunk.text)
        this.lastUserVisibleChunk = chunk
        break

      case "place": {
        // Web-only event mention: no DB place, only a structured_result ref.
        // Emit an EventMarker so the existing event-resolution path picks it
        // up — same shape Message#build_markers uses for persisted messages.
        if (
          !chunk.place &&
          chunk.structured_result &&
          /^events:\d+$/.test(chunk.structured_result)
        ) {
          this.markers = [
            ...this.markers,
            {
              type: "event",
              start: chunk.start,
              end: chunk.end,
              structured_result: chunk.structured_result,
            },
          ]
          break
        }
        if (
          chunk.place &&
          !this.places.find((e) => e.entity_ref === chunk.place?.entity_ref)
        ) {
          this.places = [...this.places, chunk.place]
        }
        const placeMarker = {
          type: "place" as const,
          start: chunk.start,
          end: chunk.end,
          ref: chunk.place?.entity_ref,
        }
        this.markers = [...this.markers, placeMarker]
        break
      }

      case "inspiration":
        if (
          chunk.inspiration &&
          !this.inspirations.find(
            (e) => e.entity_ref === chunk.inspiration?.entity_ref
          )
        ) {
          this.inspirations = [...this.inspirations, chunk.inspiration]
        }
        this.markers = [
          ...this.markers,
          {
            type: "inspiration",
            start: chunk.start,
            end: chunk.end,
            ref: chunk.inspiration?.entity_ref,
          },
        ]
        break

      case "airline":
        if (
          chunk.airline &&
          !this.airlines.find((e) => e.ref === chunk.airline?.ref)
        ) {
          this.airlines = [...this.airlines, chunk.airline]
        }
        this.markers = [
          ...this.markers,
          {
            type: "airline",
            start: chunk.start,
            end: chunk.end,
            ref: chunk.airline?.ref,
          },
        ]
        break

      case "collection":
        if (
          chunk.collection &&
          !this.collections.find((e) => e.ref === chunk.collection?.ref)
        ) {
          this.collections = [...this.collections, chunk.collection]
        }
        this.markers = [
          ...this.markers,
          {
            type: "collection",
            start: chunk.start,
            end: chunk.end,
            ref: chunk.collection?.ref,
          },
        ]
        break

      case "flight":
        this.markers = [
          ...this.markers,
          {
            type: "flight",
            start: chunk.start,
            end: chunk.end,
            flight_id: chunk.flight_id,
            flight_details: chunk.flight_details,
          },
        ]
        break

      // currently handled by the streamed-message-listener
      case "flight_search":
        break

      case "itinerary":
        this.itinerary = chunk.itinerary
        break

      case "itinerary_events":
        this.isGeneratingItinerary = true
        break

      case "itinerary_day_title":
        this.isGeneratingItinerary = true
        break

      case "chat":
        this.chat = chunk.chat
        this.trip = undefined // take the trip in the chat
        break

      case "trip":
        this.trip = chunk.trip
        break

      case "delay":
        this.lastUserVisibleChunk = chunk
        if (
          chunk.delay_reason === "generate_itinerary" ||
          chunk.delay_reason === "update_itinerary"
        ) {
          this.isGeneratingItinerary = true
        }
        this.updateActivityFeed(chunk)
        break

      case "action":
        // performing the action is handled by the streamed-message-listener
        if (chunk.action.type === "trip_change") {
          this.tripChange = chunk.action.trip_change
        }
        break

      case "search_geo_changed":
        this.searchGeo = chunk.geo
        break

      case "intent":
        this.intent = chunk.intent
        break

      case "pagination_info":
        // Pagination info is handled by the explore stream listener
        break

      case "structured_results": {
        // Each emission carries the cumulative state — replace per type,
        // don't merge entries.
        const merged = { ...(this.structuredResults ?? {}) }
        if (chunk.structured_results.events) {
          merged.events = chunk.structured_results.events
        }
        if (chunk.structured_results.trails) {
          merged.trails = chunk.structured_results.trails
        }
        this.structuredResults = merged
        break
      }

      case "ui_signal":
        this.processUiSignalChunk(chunk)
        break

      default:
        warnUnreachable(type)
        break
    }
  }

  private processUiSignalChunk(chunk: UiSignalChunk) {
    const { signal_id, phase, signal_type, payload } = chunk

    if (phase === "skeleton") {
      // PHASE 1: Create marker immediately with isStreaming=true to show skeleton UI
      const signalState = {
        signalId: signal_id,
        signalType: signal_type,
        start: this.body.length, // Use current body position as start
        end: this.body.length, // Will be updated in complete phase
        payload: payload || {},
        isComplete: false,
      }
      this.uiSignals.push(signalState)

      // Create marker immediately so UI can render skeleton
      const marker = this.createMarker(signal_type, signal_id, signalState)
      if (marker) {
        this.markers = [...this.markers, marker]
      }
      return
    }

    if (phase === "complete") {
      // PHASE 2: Update existing marker with resolved data
      const signalState = this.uiSignals.find((s) => s.signalId === signal_id)
      if (!signalState) {
        console.warn(`No skeleton state found for signal_id: ${signal_id}`)
        return
      }

      // Update state with correct positions from complete phase
      signalState.start = chunk.start ?? signalState.start
      signalState.end = chunk.end ?? signalState.end
      signalState.payload = { ...signalState.payload, ...payload }
      signalState.isComplete = true

      // For entity-based signals with place data, add to places array
      if (signal_type === "card" && payload.place) {
        const place = payload.place as PlaceSummary
        if (!this.places.some((p) => p.entity_ref === place.entity_ref)) {
          this.places = [...this.places, place]
        }
      }
      if (signal_type === "card" && payload.collection) {
        const collection = payload.collection as CollectionSummary
        if (!this.collections.some((c) => c.ref === collection.ref)) {
          this.collections = [...this.collections, collection]
        }
      }

      // Update existing marker with complete data using functional map
      const updatedMarker = this.createMarker(
        signal_type,
        signal_id,
        signalState
      )
      if (updatedMarker) {
        this.markers = this.markers.map((m) =>
          m.type === "card" && m.signal_id === signal_id ? updatedMarker : m
        )
      }
    }
  }

  private createMarker(
    signalType: string,
    signalId: string,
    signalState: {
      signalId: string
      signalType: string
      start: number
      end: number
      payload: Record<string, unknown>
      isComplete: boolean
    }
  ): MessageMarker | null {
    const baseFields = {
      signal_id: signalId,
      start: signalState.start,
      end: signalState.end,
      isStreaming: !signalState.isComplete, // True during skeleton, false when complete
    }

    // Entity-based signals
    if (signalType === "card") {
      const place = signalState.payload.place as PlaceSummary | undefined
      const collection = signalState.payload.collection as
        | CollectionSummary
        | undefined
      return {
        type: "card",
        ...baseFields,
        ref: place?.entity_ref,
        collection_ref: collection?.ref,
        entity_type: signalState.payload.entity_type as string | undefined,
        at: signalState.payload.at as number | undefined,
        quote_ref: signalState.payload.quote_ref as string | undefined,
      }
    }
    return null
  }

  applyStatusSnapshot(
    snapshot: AgentStatusUpdateEventData,
    agentSessionId: number
  ) {
    this.updateActivityFeed({
      type: "delay",
      delay_reason: "flight_search",
      agent_session_id: agentSessionId,
      metric_payload: {
        ...snapshot,
        visualizations: snapshot.visualizations?.filter(
          // Flight previews are current-message UI. The snapshot is session-wide
          // and can include flight state from a previous user turn.
          (vis) =>
            vis.type !== "flight_search_preview_stack" &&
            vis.type !== "flight_note_preview_stack"
        ),
      },
    })
  }

  private updateActivityFeed(chunk: DelayChunk) {
    if (this.thinkingSkipped) return
    if (shouldSuppressActivityFeed(chunk)) return

    const ts = nowMillis()
    this.firstDelayAtMs ??= ts
    this.lastDelayAtMs = ts

    if (
      chunk.agent_session_id &&
      this.lastAgentSessionId &&
      chunk.agent_session_id !== this.lastAgentSessionId
    ) {
      this.activityFeed = []
      this.lastEntityPreviewCount = 0
      this.lastQuotePreviewCount = 0
      this.lastFeedDelayText = undefined
      this.currentMessageFlightPreviewSeen = false
    }
    if (chunk.agent_session_id) {
      this.lastAgentSessionId = chunk.agent_session_id
    }

    if (chunk.delay_site_url) {
      this.addSiteVisit(chunk.delay_site_url)
    } else if (
      chunk.delay_text &&
      chunk.delay_text !== this.lastFeedDelayText
    ) {
      this.activityFeed = [
        ...this.activityFeed,
        {
          type: "status",
          text: chunk.delay_text,
          ...(chunk.delay_icon_domains?.length
            ? { icon_domains: chunk.delay_icon_domains }
            : {}),
        },
      ]
      this.lastFeedDelayText = chunk.delay_text
    }

    const metricPayload = chunk.metric_payload
    const visualizations = metricPayload?.visualizations
    if (!visualizations) return
    const isCurrentMessageFlightPreviewSource =
      metricPayload.source === "flight_search_params_created" ||
      metricPayload.source === "direct_flight_search"
    const isCurrentMessageFlightNoteSource =
      metricPayload.source === "flight_quote_created" ||
      metricPayload.source === "direct_flight_search"

    for (const vis of visualizations) {
      if (isEntityPreviewVisualization(vis) && vis.items.length > 0) {
        const entityItems: AgentStatusEntityPreviewItem[] =
          vis.items.filter(isEntityPreviewItem)
        if (entityItems.length > this.lastEntityPreviewCount) {
          this.lastEntityPreviewCount = entityItems.length
          this.replaceOrPushFeedEntry({
            type: "entity_preview",
            items: entityItems,
            title: vis.title,
            area_count: vis.area_count,
            unavailable_pct: vis.unavailable_pct,
          })
        }
      }

      if (isQuotePreviewVisualization(vis) && vis.items.length > 0) {
        const quoteItems: AgentStatusQuotePreviewItem[] =
          vis.items.filter(isQuotePreviewItem)
        if (quoteItems.length > this.lastQuotePreviewCount) {
          this.lastQuotePreviewCount = quoteItems.length
          this.replaceOrPushFeedEntry({
            type: "quote_preview",
            items: quoteItems,
            title: vis.title,
          })
        }
      }

      if (
        isFlightSearchPreviewVisualization(vis) &&
        vis.items.length > 0 &&
        (isCurrentMessageFlightPreviewSource ||
          this.currentMessageFlightPreviewSeen)
      ) {
        const flightItems: AgentStatusFlightSearchPreviewItem[] =
          vis.items.filter(isFlightSearchPreviewItem)
        if (flightItems.length > 0) {
          this.currentMessageFlightPreviewSeen = true
          this.replaceOrPushFeedEntry({
            type: "flight_preview",
            items: flightItems,
            title: vis.title,
          })
        }
      }

      if (isLoyaltyProgressVisualization(vis)) {
        this.replaceOrPushFeedEntry({
          type: "loyalty_progress",
          hotels_weighed: vis.hotels_weighed,
          cash_wins: vis.cash_wins,
          points_wins: vis.points_wins,
          quotes_with_points: vis.quotes_with_points,
          ...(vis.icon_domains?.length
            ? { icon_domains: vis.icon_domains }
            : {}),
        })
      }

      if (
        isFlightNotePreviewVisualization(vis) &&
        vis.items.length > 0 &&
        (isCurrentMessageFlightNoteSource ||
          this.currentMessageFlightPreviewSeen)
      ) {
        const noteItems: AgentStatusFlightNotePreviewItem[] = vis.items.filter(
          isFlightNotePreviewItem
        )
        if (noteItems.length > 0) {
          this.replaceOrPushFeedEntry({
            type: "flight_note_preview",
            items: noteItems,
            title: vis.title,
            total_count: vis.total_count,
            max_display: vis.max_display,
          })
        }
      }
    }
  }

  private replaceOrPushFeedEntry(entry: ActivityFeedEntry) {
    const lastIndex = this.activityFeed.findLastIndex(
      (e) => e.type === entry.type
    )
    if (lastIndex >= 0) {
      this.activityFeed = this.activityFeed.map((e, i) =>
        i === lastIndex ? entry : e
      )
    } else {
      this.activityFeed = [...this.activityFeed, entry]
    }
  }

  /** Pages collect in the trailing site_visits entry, mirroring ActivityFeedAccumulator. */
  private addSiteVisit(url: string) {
    const last = this.activityFeed.at(-1)
    if (last?.type === "site_visits") {
      if (last.urls.includes(url)) return
      this.activityFeed = [
        ...this.activityFeed.slice(0, -1),
        { type: "site_visits", urls: [...last.urls, url] },
      ]
    } else {
      this.activityFeed = [
        ...this.activityFeed,
        { type: "site_visits", urls: [url] },
      ]
    }
  }

  buildInProgressMessage(): InProgressMessage {
    // If the last chunk is a delay, we can expect a pause in streaming.
    // The delay chunk could include info about the delay -- possibly
    // useful for debugging, or to give an expected duration, which
    // we could use to extend timeouts -- but for now we'll just set a
    // flag to signal to the UI that we're still working.
    const delayReason =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_reason
        : undefined

    const delayText =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_text
        : undefined

    const delayQuip =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_quip
        : undefined

    const delayPlace =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_place
        : undefined

    const delayPercent =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_percent
        : undefined

    const delayComplication =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.complication_found
        : undefined

    const delayInterruptible =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_interruptible
        : undefined

    const delayInterruptibleTool =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_interruptible_tool
        : undefined

    const delayInterruptibleSessionId =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.delay_interruptible_session_id
        : undefined

    const agentSessionId =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.agent_session_id
        : undefined

    const metricPayload =
      this.lastUserVisibleChunk?.type === "delay"
        ? this.lastUserVisibleChunk.metric_payload
        : undefined

    return {
      id: "in-progress",
      type: "bot",
      timestamp: now(),
      body: this.body,
      markers: this.markers,
      places: this.places,
      inspirations: this.inspirations,
      airlines: this.airlines,
      collections: this.collections,
      itinerary: this.itinerary,
      isGeneratingItinerary: this.isGeneratingItinerary,
      chat: this.chat,
      trip: this.trip,
      delayReason,
      delayQuip,
      delayText,
      delayPlace,
      delayPercent,
      delayComplication,
      delayInterruptible,
      delayInterruptibleTool,
      delayInterruptibleSessionId,
      agentSessionId,
      metricPayload,
      activityFeed:
        this.activityFeed.length > 0 ? this.activityFeed : undefined,
      thinkingDurationSeconds:
        this.firstDelayAtMs && this.lastDelayAtMs
          ? Math.round((this.lastDelayAtMs - this.firstDelayAtMs) / 1000)
          : undefined,
      sub_type: this.sub_type,
      searchGeo: this.searchGeo,
      intent: this.intent,
      structured_results: this.structuredResults,
    }
  }
}

function isEntityPreviewItem(
  item: unknown
): item is AgentStatusEntityPreviewItem {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false
  return (
    typeof Reflect.get(item, "entity_id") === "string" &&
    typeof Reflect.get(item, "entity_ref") === "string"
  )
}

function isQuotePreviewItem(
  item: unknown
): item is AgentStatusQuotePreviewItem {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false
  return (
    typeof Reflect.get(item, "quote_ref") === "string" &&
    typeof Reflect.get(item, "entity_ref") === "string"
  )
}

function isEntityPreviewVisualization(
  vis: AgentStatusVisualization
): vis is AgentStatusEntityPreviewVisualization {
  return vis.type === "entity_preview_stack" && Array.isArray(vis.items)
}

function isQuotePreviewVisualization(
  vis: AgentStatusVisualization
): vis is AgentStatusQuotePreviewVisualization {
  return vis.type === "quote_preview_stack" && Array.isArray(vis.items)
}

function isFlightSearchPreviewVisualization(
  vis: AgentStatusVisualization
): vis is AgentStatusFlightSearchPreviewVisualization {
  return vis.type === "flight_search_preview_stack" && Array.isArray(vis.items)
}

function isLoyaltyProgressVisualization(
  vis: AgentStatusVisualization
): vis is AgentStatusLoyaltyProgressVisualization {
  return vis.type === "loyalty_progress"
}

function isFlightNotePreviewVisualization(
  vis: AgentStatusVisualization
): vis is AgentStatusFlightNotePreviewVisualization {
  return vis.type === "flight_note_preview_stack" && Array.isArray(vis.items)
}

function isFlightSearchPreviewItem(
  item: unknown
): item is AgentStatusFlightSearchPreviewItem {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false
  return typeof Reflect.get(item, "search_id") === "number"
}

function isFlightNotePreviewItem(
  item: unknown
): item is AgentStatusFlightNotePreviewItem {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false
  return typeof Reflect.get(item, "result_id") === "number"
}
